import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { DatePicker } from "@/components/ui/date-picker";
import { useNotification } from "@/shared/hooks/useNotification";
import { useMarkEntryReadyForDispatch } from "../../hooks/useSowingLifecycle";
import type { StockEntryPipelineDto } from "../../services/sowingLifecycleApi";

interface Props {
  entry: StockEntryPipelineDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const MarkEntryReadyDialog = ({ entry, open, onOpenChange, onSuccess }: Props) => {
  const notify = useNotification();
  const mutation = useMarkEntryReadyForDispatch();
  const [actualReadyDate, setActualReadyDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!actualReadyDate) {
      notify.error("Pick the actual ready date");
      return;
    }
    mutation.mutate(
      {
        entryId: entry.id,
        payload: {
          actualReadyDate: new Date(actualReadyDate).toISOString(),
          notes: notes || undefined,
        },
      },
      {
        onSuccess: () => {
          notify.success("Stock entry marked as ready for dispatch");
          onSuccess();
        },
        onError: (err: any) =>
          notify.error(err.response?.data?.message ?? "Failed to mark as ready"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark Ready for Dispatch</DialogTitle>
          <DialogDescription>
            {entry.instructionNumber} — {entry.cropTypeName}
            {entry.variety ? ` · ${entry.variety}` : ""} ({entry.availableQuantity} available)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Greenhouse
              </p>
              <p className="font-medium">{entry.greenhouseName ?? "—"}</p>
            </div>
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Entry Date
              </p>
              <p className="font-medium">
                {new Date(entry.entryDate).toLocaleDateString()}
              </p>
            </div>
            {entry.expectedReadyDate && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Expected Ready
                </p>
                <p className="font-medium">
                  {new Date(entry.expectedReadyDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          <Field>
            <FieldLabel>
              Actual Ready Date <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              value={actualReadyDate}
              onChange={(v) => setActualReadyDate(v)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Maturity notes (optional)"
              disabled={mutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Mark Ready"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
